import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';

import { DEFAULT_USER_COLUMNS } from '../../../../../../shared/constants';
import { useLocalize } from '../../../../../../shared/hooks';
import { useLazyGetColumnPickerQuery } from '../../../../../ViolationsTab/data-layer/violationsTabApi';
import { type ColumnPickerState } from '../../../../../ViolationsTab/data-layer/violationsTabTypes';
import { detailsActions } from '../../../../data-store/detailsSlice';

/**
 * Custom hook to initialize and manage column picker configuration for the Users tab view.
 *
 * This hook is responsible for fetching column definitions from an API or falling back to a default configuration
 * if the API fails or is unstable. The resulting list of columns is processed to apply localization, determine default selections,
 * and then dispatched to the Redux store to be used in the UI.
 *
 * @returns {{
 *   initializeViewColumns: () => Promise<boolean>
 * }} An object containing a function `initializeViewColumns` that initializes the column picker state and returns a promise that resolves to `true` upon success.
 *
 * @example
 * const { initializeViewColumns } = useUsersColumnPicker();
 * useEffect(() => {
 *   initializeViewColumns();
 * }, []);
 */
const useUsersColumnPicker = () => {
  const l = useLocalize();
  const dispatch = useDispatch();

  const defaultColumns = useMemo(() => DEFAULT_USER_COLUMNS, []);

  // Map API column IDs to actual data field names
  const columnIdToFieldMapping = useMemo(
    () =>
      ({
        userName: 'userName',
        displayName: 'displayName',
        firstName: 'firstName',
        lastName: 'lastName',
        email: 'email',
        status: 'status',
        employeeId: 'employeeId',
        title: 'title',
        identityType: 'identityType',
      }) as Record<string, string>,
    []
  );

  const [fetchColumnPicker] = useLazyGetColumnPickerQuery();

  const initializeViewColumns = useCallback(async () => {
    try {
      const response = (await fetchColumnPicker(
        'user_list'
      ).unwrap()) as Record<string, ColumnPickerState[]>;

      const columnsList: ColumnPickerState[] = Object.values(response).flat();
      const modifiedColumnsList = columnsList
        .filter(
          (column) =>
            column.id !== 'userName' &&
            column.id !== 'departmentName' &&
            column.id !== 'identityType'
        )
        .map((column) => ({
          label: l(column.localizationKey),
          value: columnIdToFieldMapping[column.id] || column.id,
          isChecked: column.isDefault,
          isDefault: defaultColumns.includes(column.id),
          isDisabled: false,
        }))
        .sort((b, a) => Number(a.isChecked) - Number(b.isChecked));

      dispatch(
        detailsActions.setEntitlementUsersActiveColumns(modifiedColumnsList)
      );

      return true;
    } catch (error) {
      Logger.log('Error fetching users column picker');

      return false;
    }
  }, [l, defaultColumns, dispatch, fetchColumnPicker, columnIdToFieldMapping]);

  return { initializeViewColumns };
};

export default useUsersColumnPicker;
